import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Sunrise, Moon, CheckCircle2, Circle, Wallet, Car, Users, Clock, ArrowRight, AlertTriangle, Loader2, Play, Settings2, Eye, EyeOff, ArrowUp, ArrowDown, Dumbbell, Landmark, Gauge } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Modal from "@/components/Modal";
import { mfTotals, DEFAULT_STATE as MF_DEFAULT_STATE } from "@/components/MoneyFlex";
import { useTheme } from "@/context/ThemeContext";

const TAX_TIPS = [
  "Q4 estimated taxes are due Jan 15 — pay via IRS Direct Pay to avoid an underpayment penalty.",
  "1099-NEC and W-2 forms should arrive by Jan 31. Gather them now so nothing's missing at filing.",
  "Set your home-office square footage. The simplified method deducts $5/sq ft, up to 300 sq ft ($1,500).",
  "April 15: individual returns and Q1 estimated taxes are both due. File or extend — but still pay what you owe.",
  "Log business miles as you drive. The standard mileage rate often beats tracking actual vehicle costs.",
  "Q2 estimated taxes are due June 15. Base it on your year-to-date net profit to stay penalty-safe.",
  "Mid-year check: reconcile business bank and card statements monthly so no deduction slips through.",
  "Business meals are 50% deductible; entertainment is 0%. Note who, what, and why on every receipt.",
  "Q3 estimated taxes are due Sept 15. Adjust for any big summer income swings.",
  "Extended returns are due Oct 15. A SEP-IRA can still cut this year's taxable income.",
  "Time year-end equipment buys — Section 179 can let you expense qualifying purchases this year.",
  "Make last-minute deductible purchases and retirement contributions before Dec 31 closes the tax year.",
];

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CC_CSS = `
[data-testid="morning-briefing"].cc{background:#05090c;position:relative;overflow-x:hidden;}
[data-testid="morning-briefing"].cc::before{content:"";position:fixed;inset:0;pointer-events:none;z-index:0;background:radial-gradient(1200px 600px at 72% -12%,rgba(16,185,129,0.12),transparent 60%),radial-gradient(900px 520px at 8% 112%,rgba(56,189,248,0.09),transparent 60%);animation:ccPulse 8s ease-in-out infinite alternate;}
[data-testid="morning-briefing"].cc::after{content:"";position:fixed;inset:0;pointer-events:none;z-index:0;background-image:radial-gradient(2px 2px at 20% 30%,rgba(52,211,153,0.35),transparent),radial-gradient(2px 2px at 62% 68%,rgba(45,212,191,0.30),transparent),radial-gradient(1.6px 1.6px at 82% 22%,rgba(250,204,21,0.28),transparent),radial-gradient(1.6px 1.6px at 34% 86%,rgba(52,211,153,0.25),transparent),radial-gradient(1.4px 1.4px at 90% 60%,rgba(56,189,248,0.22),transparent);background-size:100% 100%;animation:ccDrift 26s linear infinite;opacity:.55;}
@keyframes ccPulse{0%{opacity:.5}100%{opacity:1}}
@keyframes ccDrift{0%{transform:translateY(0)}100%{transform:translateY(-46px)}}
[data-testid="morning-briefing"].cc header{position:relative;z-index:3;}
[data-testid="morning-briefing"].cc main{position:relative;z-index:1;--text:#eafff5;--text-2:#8fb3a8;--line:rgba(120,210,180,0.18);--accent:#10b981;--danger:#f87171;}
[data-testid="morning-briefing"].cc main .bg-white{background:rgba(13,23,20,0.55)!important;backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);}
[data-testid="morning-briefing"].cc main section{border-radius:12px;transition:box-shadow .35s ease,border-color .35s ease,transform .35s ease;}
[data-testid="morning-briefing"].cc main section:hover{border-color:rgba(45,212,191,0.55)!important;box-shadow:0 0 26px rgba(45,212,191,0.22),inset 0 0 0 1px rgba(45,212,191,0.15);}
[data-testid="morning-briefing"].cc main .font-mono:not(.cc-gold-positive){color:#f2fffb!important;letter-spacing:.02em;}
[data-testid="morning-briefing"].cc main .font-mono.cc-gold-positive{letter-spacing:.02em;}
[data-testid="morning-briefing"].cc main .text-emerald-600{color:#34d399!important;text-shadow:0 0 12px rgba(52,211,153,0.35);}
[data-testid="morning-briefing"].cc main [data-testid="briefing-net"]:not(.cc-gold-positive){color:#ffffff!important;text-shadow:0 0 14px rgba(255,255,255,0.28);}
[data-testid="morning-briefing"].cc main [data-testid="briefing-miles-line"]{color:#eafff5;}
[data-testid="morning-briefing"].cc main .font-serif{color:#eafff5;}
[data-testid="morning-briefing"].cc main [data-testid="briefing-greeting"]{background:rgba(16,185,129,0.07)!important;border-color:rgba(45,212,191,0.28)!important;box-shadow:0 0 30px rgba(16,185,129,0.10);}
[data-testid="morning-briefing"].cc header{background:rgba(4,8,11,0.92)!important;border-bottom-color:rgba(120,210,180,0.20)!important;--text:#eafff5;--text-2:#9fc7ba;--line:rgba(120,210,180,0.18);--accent:#10b981;color:#eafff5;}
[data-testid="morning-briefing"].cc header .bg-white{background:rgba(13,23,20,0.55)!important;color:#eafff5;}
[data-testid="morning-briefing"].cc header [data-testid="brand-home"] span,[data-testid="morning-briefing"].cc header [data-testid="app-tagline"]{color:#9fc7ba!important;}
[data-testid="morning-briefing"].cc header [data-testid="user-dropdown"],[data-testid="morning-briefing"].cc header [data-testid="notif-dropdown"]{background:#0a1310!important;border-color:rgba(120,210,180,0.22)!important;color:#eafff5;}
[data-testid="morning-briefing"].cc header [data-testid="user-dropdown"] .bg-white,[data-testid="morning-briefing"].cc header [data-testid="notif-dropdown"] .bg-white{background:#0a1310!important;}
[data-testid="morning-briefing"].cc header [data-testid="user-dropdown"] button:hover,[data-testid="morning-briefing"].cc header [data-testid="notif-dropdown"] button:hover{background:rgba(16,185,129,0.14)!important;}
`;

const ALL_CARDS = ["today", "money", "miles", "upcoming", "team", "credit"];
const CARD_LABELS = { today: "Today / Tomorrow", money: "Money Snapshot", miles: "Cash Miles", upcoming: "Upcoming", team: "Team Summary", credit: "Credit Score" };

const CREDIT_RANGES = [
  { min: 300, max: 579, label: "Poor", color: "text-rose-400", bar: "bg-rose-500" },
  { min: 580, max: 669, label: "Fair", color: "text-orange-400", bar: "bg-orange-500" },
  { min: 670, max: 739, label: "Good", color: "text-yellow-400", bar: "bg-yellow-500" },
  { min: 740, max: 799, label: "Very Good", color: "text-emerald-400", bar: "bg-emerald-500" },
  { min: 800, max: 850, label: "Exceptional", color: "text-emerald-300", bar: "bg-emerald-400" },
];
const creditRangeOf = (s) => CREDIT_RANGES.find((r) => s >= r.min && s <= r.max) || CREDIT_RANGES[0];

function salutation() {
  const h = new Date().getHours();
  if (h < 12) return { text: "Good morning", icon: Sunrise };
  if (h < 18) return { text: "Good afternoon", icon: Sun };
  return { text: "Good evening", icon: Moon };
}

function Card({ title, icon: Icon, onNav, children, testid, headerRight }) {
  return (
    <section data-testid={testid} className="border border-[var(--line)] bg-white flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--line)]">
        <h2 className="font-serif text-lg tracking-tight flex items-center gap-2"><Icon className="w-4 h-4 text-[var(--accent)]" /> {title}</h2>
        {headerRight}
      </div>
      <div className="p-4 flex-1 min-h-0">{children}</div>
      {onNav && (
        <button data-testid={`${testid}-details`} onClick={onNav} className="flex items-center justify-between gap-1 px-4 py-2.5 border-t border-[var(--line)] text-xs text-[var(--text-2)] hover:text-[var(--accent)] transition-colors">
          View Full Details <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </section>
  );
}

export default function MorningBriefing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [day, setDay] = useState(new Date().getHours() >= 18 ? "tomorrow" : "today");
  const [customize, setCustomize] = useState(false);
  const [order, setOrder] = useState(ALL_CARDS);
  const [hidden, setHidden] = useState([]);
  const { cc, toggleCc } = useTheme();
  const [mf, setMf] = useState(null);
  const [credit, setCredit] = useState(null);
  const prefsTimer = useRef(null);

  const load = useCallback((d) => {
    api.get(`/briefing?day=${d}`).then((r) => {
      setData(r.data);
      const p = r.data.prefs || {};
      const ord = (p.order && p.order.length ? p.order : ALL_CARDS).filter((k) => ALL_CARDS.includes(k));
      ALL_CARDS.forEach((k) => { if (!ord.includes(k)) ord.push(k); });
      setOrder(ord);
      setHidden((p.hidden || []).filter((k) => [...ALL_CARDS, "greeting"].includes(k)));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(day); document.body.setAttribute("data-mode", "personal"); }, [load, day]);
  useEffect(() => { api.get("/moneyflex").then((r) => setMf(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/credit-score").then((r) => setCredit(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const refresh = () => { if (!document.hidden) load(day); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [load, day]);

  const seed = async () => { await api.post("/demo/seed"); toast.success("Demo data loaded"); load(day); };
  const reset = async () => { await api.post("/demo/reset"); toast.success("All data cleared"); load(day); };
  const complete = async (id) => { try { await api.put(`/tasks/${id}`, { completed: true }); load(day); } catch { toast.error("Could not complete"); } };
  const go = (path) => () => navigate(path);
  const startMyDay = () => navigate("/dashboard?mode=personal&tab=reminders");

  const savePrefs = (nextOrder, nextHidden) => {
    setOrder(nextOrder); setHidden(nextHidden);
    if (prefsTimer.current) clearTimeout(prefsTimer.current);
    prefsTimer.current = setTimeout(() => { api.put("/prefs/briefing", { order: nextOrder, hidden: nextHidden }).catch(() => {}); }, 400);
  };
  const toggleHide = (k) => { const nh = hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k]; savePrefs(order, nh); };
  const move = (k, dir) => {
    const i = order.indexOf(k); const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const no = [...order]; [no[i], no[j]] = [no[j], no[i]]; savePrefs(no, hidden);
  };

  const sal = salutation();
  const SalIcon = sal.icon;
  const greetingText = data ? `${sal.text}${data.name ? ", " + data.name : ""} — ${data.greeting_body}` : "";

  const mfScore = (() => {
    if (!mf || !mf.state) return { pct: 0, label: "Log your week", color: "text-[var(--text-2)]", barClass: "bg-[var(--text-2)]/40", hint: "Add your net income in MoneyFlex to see your weekly allocation score." };
    const t = mfTotals({ ...MF_DEFAULT_STATE(), ...mf.state });
    if (t.net <= 0) return { pct: 0, label: "Log your week", color: "text-[var(--text-2)]", barClass: "bg-[var(--text-2)]/40", hint: "Add your net income in MoneyFlex to see your weekly allocation score." };
    const pct = Math.max(0, Math.min(100, Math.round(100 - (Math.abs(t.remaining) / t.net) * 100)));
    if (t.remaining < 0) return { pct, label: "Needs Attention", color: "text-rose-500", barClass: "bg-rose-500", hint: `Over budget by ${money(Math.abs(t.remaining))}. Trim a category to get back on plan.` };
    if (pct >= 80) return { pct, label: "On Track", color: "text-emerald-400", barClass: "bg-emerald-500", hint: `Nicely planned — ${money(t.remaining)} still unallocated this week.` };
    if (pct >= 50) return { pct, label: "Solid Progress", color: "text-teal-400", barClass: "bg-teal-500", hint: `${money(t.remaining)} left to assign — put every dollar to work.` };
    return { pct, label: "Getting There", color: "text-amber-400", barClass: "bg-amber-500", hint: `${money(t.remaining)} unassigned — allocate more toward savings or debt.` };
  })();

  const taxTip = TAX_TIPS[new Date().getMonth()];
  const monthName = new Date().toLocaleString(undefined, { month: "long" });

  const WEEKLY_MULT = { daily: 7, weekly: 1, biweekly: 0.5, monthly: 0.25 };
  const toWk = (a, f) => (parseFloat(a) || 0) * (WEEKLY_MULT[f] ?? 1);
  const incomeWeek = (() => {
    const st = mf && mf.state;
    if (!st) return null;
    const primary = toWk(st.net, st.primaryFreq || "weekly");
    const gigs = [];
    const gl = st.gigs || [];
    if (gl.length) {
      gl.forEach((g) => { const w = toWk(g.amount, g.freq || "weekly"); if (w > 0) gigs.push({ label: (g.label || "Gig").trim() || "Gig", weekly: w }); });
    } else {
      const sec = toWk(st.secondary, st.secondaryFreq || "weekly");
      if (sec > 0) gigs.push({ label: "Side gig", weekly: sec });
    }
    const total = primary + gigs.reduce((s, g) => s + g.weekly, 0);
    return total > 0 ? { primary, gigs, total } : null;
  })();

  const paydayInfo = (() => {
    const st = mf && mf.state;
    if (!st || !st.payday) return null;
    const div = { weekly: 4, biweekly: 2, monthly: 1 }[st.payFreq || "biweekly"] || 2;
    const tt = mfTotals({ ...MF_DEFAULT_STATE(), ...st });
    const amt = (tt.mandatory + (st.sdSeparate ? 0 : tt.savings + tt.debt)) / div;
    const pd = new Date(st.payday + "T00:00:00");
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const days = Math.round((pd - now) / 86400000);
    if (days < 0 || amt <= 0) return null;
    return { days, amt };
  })();

  const creditNudge = (() => {
    if (!credit || !credit.goal || !(credit.entries || []).length) return null;
    const cur = [...credit.entries].sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(-1)[0].score;
    const gap = credit.goal - cur;
    return gap > 0 && gap <= 20 ? { gap, goal: credit.goal } : null;
  })();

  const creditSnap = (() => {
    if (!credit || !(credit.entries || []).length) return null;
    const latest = [...credit.entries].sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(-1)[0];
    const pct = Math.max(0, Math.min(100, Math.round(((latest.score - 300) / 550) * 100)));
    return { score: latest.score, date: latest.date, range: creditRangeOf(latest.score), pct };
  })();

  const renderCard = (key) => {
    if (hidden.includes(key)) return null;
    if (key === "team" && data && !data.team.has_team) return null;
    if (!data) return null;
    if (key === "today") {
      return (
        <Card key="today" testid="briefing-today" title={day === "tomorrow" ? "Tomorrow" : "Today"} icon={CheckCircle2} onNav={go("/dashboard?mode=personal&tab=reminders")}>
          {data.today.length === 0 ? (
            <p className="text-sm text-[var(--text-2)]">Nothing due {day === "tomorrow" ? "tomorrow" : "today"}. Enjoy the calm.</p>
          ) : (
            <ul className="space-y-1 max-h-[220px] overflow-y-auto scroll-thin -mx-1 px-1" data-testid="briefing-today-list">
              {data.today.map((t) => (
                <li key={t.id} className="flex items-center gap-3 py-1.5 group">
                  <button data-testid={`briefing-complete-${t.id}`} onClick={() => complete(t.id)} title="Mark complete" className="shrink-0 text-[var(--text-2)] hover:text-[var(--accent)] transition-colors">
                    <Circle className="w-5 h-5 group-hover:hidden" />
                    <CheckCircle2 className="w-5 h-5 hidden group-hover:block" />
                  </button>
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{t.title}{t.kind === "bill" && t.amount ? ` · ${money2(t.amount)}` : ""}</p></div>
                  <span className="shrink-0 font-mono text-xs text-[var(--text-2)]">{t.time || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      );
    }
    if (key === "money") {
      return (
        <Card key="money" testid="briefing-money" title="Money Snapshot" icon={Wallet} onNav={go("/snapbudget")}>
          {data.money.locked ? (
            <p className="text-sm text-[var(--text-2)]">Unlock SnapBudget to see this month's income, expenses & net.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2 text-center" data-testid="briefing-money-nums">
              <div><p className="label-mono text-[var(--text-2)]">Income</p><p data-testid="briefing-income" className="font-mono text-lg font-bold text-emerald-600">{money(data.money.income)}</p></div>
              <div><p className="label-mono text-[var(--text-2)]">Expenses</p><p data-testid="briefing-expenses" className="font-mono text-lg font-bold text-[var(--danger)]">{money(data.money.expenses)}</p></div>
              <div><p className="label-mono text-[var(--text-2)]">Net Fluid</p><p data-testid="briefing-net" className={`font-mono text-lg font-bold ${data.money.net >= 0 ? (cc ? "cc-gold-positive" : "text-[var(--text)]") : "text-[var(--danger)]"}`}>{money(data.money.net)}</p></div>
            </div>
          )}
          {incomeWeek && (
            <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="briefing-income-week">
              <div className="flex items-center justify-between">
                <span className="label-mono text-[var(--text-2)]">Weekly income</span>
                <span className="font-mono text-base font-bold text-emerald-600" data-testid="briefing-income-week-total">{money(incomeWeek.total)}</span>
              </div>
              <div className="mt-1.5 space-y-1">
                <div className="flex items-center justify-between text-[11px]"><span className="text-[var(--text-2)]">Primary</span><span className="font-mono">{money(incomeWeek.primary)}</span></div>
                {incomeWeek.gigs.map((g, i) => (
                  <div key={i} data-testid="briefing-income-gig" className="flex items-center justify-between text-[11px]"><span className="text-[var(--text-2)] truncate pr-2">{g.label}</span><span className="font-mono">{money(g.weekly)}</span></div>
                ))}
              </div>
            </div>
          )}
        </Card>
      );
    }
    if (key === "miles") {
      return (
        <Card key="miles" testid="briefing-miles" title="Cash Miles" icon={Car} onNav={go("/dashboard?mode=personal&tab=miles")}>
          <p className="text-sm" data-testid="briefing-miles-line">
            <span className="font-mono text-lg font-bold">{data.miles.miles} mi</span>
            <span className="text-[var(--text-2)]"> this month · </span>
            <span className="font-mono text-lg font-bold text-[var(--accent)]">{money2(data.miles.credit)}</span>
            <span className="text-[var(--text-2)]"> est. tax credit</span>
          </p>
        </Card>
      );
    }
    if (key === "upcoming") {
      return (
        <Card key="upcoming" testid="briefing-upcoming" title="Upcoming · next 24h" icon={Clock} onNav={go("/dashboard?mode=personal&tab=reminders")}>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-[var(--text-2)]">Nothing scheduled in the next 24 hours.</p>
          ) : (
            <ul className="space-y-2" data-testid="briefing-upcoming-list">
              {data.upcoming.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm truncate">{t.title}</span>
                  <span className="shrink-0 font-mono text-xs text-[var(--text-2)]">{(t.next_run || "").slice(5, 16).replace("T", " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      );
    }
    if (key === "team") {
      return (
        <Card key="team" testid="briefing-team" title="Team · overdue" icon={Users} onNav={go("/dashboard?mode=business")}>
          {data.team.overdue_count === 0 ? (
            <p className="text-sm text-[var(--text-2)]">No overdue team tasks. Everyone's on track.</p>
          ) : (
            <>
              <p className="text-sm text-[var(--danger)] flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" /> {data.team.overdue_count} overdue task{data.team.overdue_count === 1 ? "" : "s"}</p>
              <ul className="space-y-1.5" data-testid="briefing-team-list">
                {data.team.overdue.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-3">
                    <span className="text-sm truncate">{t.title} <span className="text-[var(--text-2)]">· {t.assignee}</span></span>
                    <span className="shrink-0 font-mono text-xs text-[var(--danger)]">{t.due}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      );
    }
    if (key === "credit") {
      return (
        <Card key="credit" testid="briefing-credit" title="Credit Score" icon={Gauge} onNav={go("/snapbudget")}>
          {!creditSnap ? (
            <div className="text-sm text-[var(--text-2)]" data-testid="briefing-credit-empty">
              <p>Add your credit score to start tracking.</p>
              <button data-testid="briefing-credit-empty-link" onClick={go("/snapbudget")} className="mt-2 text-[var(--accent)] hover:underline">Open Credit Score Tracker →</button>
            </div>
          ) : (
            <div data-testid="briefing-credit-body">
              <div className="flex items-end justify-between gap-3 mb-2.5">
                <span data-testid="briefing-credit-score" className={`font-mono text-3xl font-bold leading-none ${creditSnap.range.color}`}>{creditSnap.score}</span>
                <span data-testid="briefing-credit-range" className={`text-xs font-semibold ${creditSnap.range.color}`}>{creditSnap.range.label}</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-[var(--line)] overflow-hidden">
                <div data-testid="briefing-credit-bar" className={`h-full rounded-full transition-all duration-700 ease-out ${creditSnap.range.bar}`} style={{ width: `${creditSnap.pct}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-[var(--text-2)] mt-1"><span>300</span><span>850</span></div>
              <p data-testid="briefing-credit-updated" className="text-xs text-[var(--text-2)] mt-2.5">Updated {creditSnap.date}</p>
            </div>
          )}
        </Card>
      );
    }
    return null;
  };

  return (
    <div className={`min-h-screen app-canvas ${cc ? "cc" : ""}`} data-testid="morning-briefing">
      <style>{CC_CSS}</style>
      <Header mode="personal" setMode={(m) => navigate(`/dashboard?mode=${m}`)} overdueCount={data?.team?.overdue_count || 0} onOpenDispatch={() => navigate("/dashboard")} dispatchCount={0} onSeed={seed} onReset={reset} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
        {!data ? (
          <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" /></div>
        ) : (
          <>
            {/* AI morning message (hideable) */}
            {!hidden.includes("greeting") && (
              <div data-testid="briefing-greeting" className="mb-4 border border-[var(--accent)]/30 bg-[var(--accent)]/5 px-5 py-4 flex items-start gap-3">
                <SalIcon className="w-6 h-6 text-[var(--accent)] shrink-0 mt-0.5" />
                <p className="font-serif text-xl sm:text-2xl leading-snug text-[var(--text)] flex-1">{greetingText}</p>
              </div>
            )}

            {/* Controls (always visible) */}
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="briefing-controls">
              <button data-testid="briefing-start-day" onClick={startMyDay} className="inline-flex items-center gap-2 h-9 px-4 bg-[var(--accent)] text-white text-sm hover:opacity-90 transition-opacity">
                <Play className="w-4 h-4" /> Start my day
              </button>
              <div className="inline-flex border border-[var(--line)] rounded overflow-hidden" data-testid="briefing-day-toggle">
                <button data-testid="briefing-day-today" onClick={() => setDay("today")} className={`px-3 h-9 text-sm transition-colors ${day === "today" ? "bg-[#1A1A18] text-white" : "bg-white text-[var(--text-2)] hover:text-[var(--text)]"}`}>Today</button>
                <button data-testid="briefing-day-tomorrow" onClick={() => setDay("tomorrow")} className={`px-3 h-9 text-sm transition-colors ${day === "tomorrow" ? "bg-[#1A1A18] text-white" : "bg-white text-[var(--text-2)] hover:text-[var(--text)]"}`}>Tomorrow</button>
              </div>
              <button data-testid="briefing-customize" onClick={() => setCustomize(true)} title="Personalize your briefing sections" className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#1A1A18] text-white text-sm hover:bg-[var(--accent)] transition-colors">
                <Settings2 className="w-4 h-4" /> Personalize
              </button>
              <button data-testid="briefing-theme-toggle" onClick={toggleCc} title="Switch between Command Center dark mode and light mode" className="inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--line)] bg-white text-sm hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors">
                {cc ? <><Sun className="w-4 h-4" /> Light mode</> : <><Moon className="w-4 h-4" /> Command Center</>}
              </button>
            </div>

            {paydayInfo && (
              <div data-testid="briefing-payday" onClick={go("/dashboard?mode=personal&tab=moneyflex")} className="mb-4 flex items-center gap-3 border border-emerald-400/40 bg-emerald-400/5 px-4 py-3 cursor-pointer hover:border-emerald-400 transition-colors">
                <Wallet className="w-5 h-5 text-emerald-400 shrink-0" />
                <p className="text-sm text-[var(--text)]">{paydayInfo.days === 0 ? "Payday is today" : paydayInfo.days === 1 ? "Payday tomorrow" : `Payday in ${paydayInfo.days} days`} — protect <span className="font-mono font-bold text-emerald-400">{money(paydayInfo.amt)}</span> from this check.</p>
                <ArrowRight className="w-4 h-4 text-[var(--text-2)] ml-auto shrink-0" />
              </div>
            )}

            {creditNudge && (
              <div data-testid="briefing-credit-nudge" onClick={go("/snapbudget")} className="mb-4 flex items-center gap-3 border border-amber-400/40 bg-amber-400/5 px-4 py-3 cursor-pointer hover:border-amber-400 transition-colors">
                <Gauge className="w-5 h-5 text-amber-400 shrink-0" />
                <p className="text-sm text-[var(--text)]">Credit goal within reach — you're <span className="font-mono font-bold text-amber-400">{creditNudge.gap}</span> point{creditNudge.gap === 1 ? "" : "s"} from your {creditNudge.goal} goal. Keep it up!</p>
                <ArrowRight className="w-4 h-4 text-[var(--text-2)] ml-auto shrink-0" />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {order.map((k) => renderCard(k))}
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <section data-testid="briefing-moneyflex-score" className="border border-[var(--line)] bg-white flex flex-col">
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--line)]">
                  <h2 className="font-serif text-lg tracking-tight flex items-center gap-2"><Dumbbell className="w-4 h-4 text-[var(--accent)]" /> MoneyFlex Weekly Score</h2>
                  <span className="label-mono text-[var(--text-2)]">{mfScore.pct}/100</span>
                </div>
                <div className="p-4 flex-1">
                  <div className="flex items-end justify-between gap-3 mb-2.5">
                    <span data-testid="briefing-mf-label" className={`text-sm font-semibold ${mfScore.color}`}>{mfScore.label}</span>
                    <span data-testid="briefing-mf-score" className="font-mono text-3xl font-bold leading-none">{mfScore.pct}</span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-[var(--line)] overflow-hidden">
                    <div data-testid="briefing-mf-bar" className={`h-full rounded-full transition-all duration-700 ease-out ${mfScore.barClass}`} style={{ width: `${mfScore.pct}%` }} />
                  </div>
                  <p className="text-xs text-[var(--text-2)] mt-3 leading-relaxed">{mfScore.hint}</p>
                </div>
                <button data-testid="briefing-mf-details" onClick={go("/dashboard?mode=personal&tab=moneyflex")} className="flex items-center justify-between gap-1 px-4 py-2.5 border-t border-[var(--line)] text-xs text-[var(--text-2)] hover:text-[var(--accent)] transition-colors">
                  Open MoneyFlex <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </section>

              <section data-testid="briefing-tax-tip" className="border border-[var(--line)] bg-white flex flex-col">
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--line)]">
                  <h2 className="font-serif text-lg tracking-tight flex items-center gap-2"><Landmark className="w-4 h-4 text-[var(--accent)]" /> Tax Clarity Hub · Quick Tip</h2>
                  <span className="label-mono text-[var(--text-2)]">{monthName}</span>
                </div>
                <div className="p-4 flex-1">
                  <p data-testid="briefing-tax-tip-text" className="text-sm leading-relaxed">{taxTip}</p>
                </div>
                <button data-testid="briefing-tax-tip-details" onClick={go("/tax-hub")} className="flex items-center justify-between gap-1 px-4 py-2.5 border-t border-[var(--line)] text-xs text-[var(--text-2)] hover:text-[var(--accent)] transition-colors">
                  Open Tax Clarity Hub <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </section>
            </div>
          </>
        )}
      </main>

      <Modal open={customize} onClose={() => setCustomize(false)} testid="briefing-customize-modal" subtitle="Personalize" title="Briefing layout">
        <label className="flex items-center justify-between gap-3 border border-[var(--line)] rounded p-3 mb-4">
          <span className="text-sm flex items-center gap-2">{cc ? <Moon className="w-4 h-4 text-[var(--accent)]" /> : <Sun className="w-4 h-4 text-[var(--accent)]" />} Command Center dark mode</span>
          <button data-testid="personalize-theme-toggle" onClick={toggleCc} role="switch" aria-checked={cc} className={`relative w-11 h-6 rounded-full transition-colors ${cc ? "bg-[var(--accent)]" : "bg-[var(--line)]"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cc ? "translate-x-5" : ""}`} />
          </button>
        </label>
        <p className="text-sm text-[var(--text-2)] mb-4">Reorder or hide sections. Changes save automatically.</p>
        <ul className="space-y-2" data-testid="briefing-customize-list">
          <li className="flex items-center justify-between gap-3 border border-[var(--line)] rounded p-2.5">
            <span className={`text-sm ${hidden.includes("greeting") ? "text-[var(--text-2)] line-through" : "text-[var(--text)]"}`}>AI morning message</span>
            <button data-testid="briefing-toggle-greeting" onClick={() => toggleHide("greeting")} className="w-8 h-8 grid place-items-center border border-[var(--line)] hover:border-[var(--accent)] transition-colors">{hidden.includes("greeting") ? <EyeOff className="w-4 h-4 text-[var(--text-2)]" /> : <Eye className="w-4 h-4 text-[var(--accent)]" />}</button>
          </li>
          {order.map((k, i) => {
            const isHidden = hidden.includes(k);
            return (
              <li key={k} className="flex items-center justify-between gap-3 border border-[var(--line)] rounded p-2.5">
                <span className={`text-sm ${isHidden ? "text-[var(--text-2)] line-through" : "text-[var(--text)]"}`}>{CARD_LABELS[k]}</span>
                <div className="flex items-center gap-1">
                  <button data-testid={`briefing-move-up-${k}`} onClick={() => move(k, -1)} disabled={i === 0} className="w-8 h-8 grid place-items-center border border-[var(--line)] disabled:opacity-30 hover:border-[var(--accent)] transition-colors"><ArrowUp className="w-4 h-4" /></button>
                  <button data-testid={`briefing-move-down-${k}`} onClick={() => move(k, 1)} disabled={i === order.length - 1} className="w-8 h-8 grid place-items-center border border-[var(--line)] disabled:opacity-30 hover:border-[var(--accent)] transition-colors"><ArrowDown className="w-4 h-4" /></button>
                  <button data-testid={`briefing-toggle-${k}`} onClick={() => toggleHide(k)} className="w-8 h-8 grid place-items-center border border-[var(--line)] hover:border-[var(--accent)] transition-colors">{isHidden ? <EyeOff className="w-4 h-4 text-[var(--text-2)]" /> : <Eye className="w-4 h-4 text-[var(--accent)]" />}</button>
                </div>
              </li>
            );
          })}
        </ul>
      </Modal>
    </div>
  );
}
